import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import {
    Box,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StyledTableCell, StyledTableHead } from "../../../components/table/BaseTable/BaseTable";
import { round } from "../../../helpers/methods";
import useDialog from "../../../hooks/useDialog";
import { calculateProductPricing } from "../../../utils";
import { ControlledVatDropdown } from "./ControlledVatDropdown";
import { ProductKindDropdown } from "./ProductKindDropdown";
import { ProductPricingHistoryDialog } from "./ProductPricingHistory";
import { formatDecimalNumber } from "../../../utils";

const disabledInputSx = {
    "& .MuiInputBase-input.Mui-disabled": {
        WebkitTextFillColor: "#010a1a",
    },
};

export const ProductPricing = (props) => {
    const { control, fieldNames, readOnly = false, passportView = false, productId, isMultipleProducts=false, setValue, showProductKind=true } = props;
    const { t } = useTranslation();

    const [openPricingHistoryDialog, onOpenPricingHistoryDialog, onClosePricingHistoryDialog] =
        useDialog();

    
    const netListedPerSqm = useWatch({ control, name: fieldNames.pricing.listedNetPerSqm });

    const handleChangeMaxDiscount = (maxDiscount) => {
        const maxDiscountProc = ((100 - maxDiscount) / 100).toFixed(10)
        setValue(fieldNames.pricing.minNetPerSqm, (netListedPerSqm * maxDiscountProc).toFixed(2))
    }

    return (
        <Paper
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
                margin: "1rem",
                padding: "1rem",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-evenly",
                        gap: "1rem",
                        padding: "1rem",
                    }}
                >
                    <Typography
                        variant="h3"
                        align="left"
                        sx={{ width: "100%", fontSize: "1.5rem" }}
                    >
                        {t("products.ProductPricing.title")}
                        {productId && (
                            <CurrencyExchangeIcon
                                fontSize="large"
                                color="primary"
                                sx={{ marginLeft: "10px", paddingTop: "10px", cursor: "pointer" }}
                                onClick={onOpenPricingHistoryDialog}
                            />
                        )}
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "1rem",
                            flex: 1,
                        }}
                    >
                        <Typography>{t("products.ProductPricing.maxDiscount")}</Typography>
                        <ControlledNumericInput
                            control={control}
                            fieldName={fieldNames.pricing.maxDiscount}
                            percentage
                            readOnly={readOnly}
                            onChangeCb={handleChangeMaxDiscount}
                        />
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "1rem",
                            flex: 1,
                        }}
                    >
                        <Typography>{t("products.ProductPricing.vat")}</Typography>
                        <ControlledVatDropdown
                            control={control}
                            fieldName={fieldNames.pricing.vat}
                            readOnly={readOnly}
                        />
                    </Box>
                    { showProductKind && <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "1rem",
                            flex: 2,
                        }}
                    >
                        <Typography>{t("products.ProductPricing.kind")}{isMultipleProducts ? "" : "*"}</Typography>
                        <ProductKindDropdown
                            control={control}
                            fieldName={fieldNames.kind}
                            disabled={readOnly && !isMultipleProducts}
                            textFieldSx={{ width: "300px" }}
                        />
                    </Box>}
                </Box>
                <PricingTable
                    control={control}
                    fieldNames={{
                        ...fieldNames.pricing,
                        footage: fieldNames.footage,
                        footageFinal: fieldNames.footage,
                    }}
                    readOnly={readOnly}
                    setValue={setValue}
                    passportView={passportView}
                />
            </Box>
            {openPricingHistoryDialog && (
                <ProductPricingHistoryDialog
                    open={openPricingHistoryDialog}
                    onClose={onClosePricingHistoryDialog}
                    productId={props.productId}
                    readOnly={readOnly}
                />
            )}
        </Paper>
    );
};

const PricingTable = (props) => {
    const { control, fieldNames, readOnly = false, passportView = false, setValue } = props;
    const { t } = useTranslation();

    const netListedPerSqm = useWatch({ control, name: fieldNames.listedNetPerSqm });
    const minNetPerSqm = useWatch({ control, name: fieldNames.minNetPerSqm });
    const baseNetPerSqm = useWatch({ control, name: fieldNames.baseNetPerSqm });
    const footage = useWatch({ control, name: fieldNames.footage });
    const vatPercent = useWatch({ control, name: fieldNames.vat });
    const footageFinal = useWatch({ control, name: fieldNames.footageFinal });
    const maxDiscount = useWatch({ control, name: fieldNames.maxDiscount }); 

    const effectiveFootage =
        footageFinal !== undefined && footageFinal !== null && footageFinal !== ""
            ? footageFinal
            : footage;

    const handleChangNetListedPerSqm = (netListedPerSqm) => {
        const maxDiscountProc = ((100 - maxDiscount) / 100).toFixed(2)
        setValue(fieldNames.minNetPerSqm, (netListedPerSqm * maxDiscountProc).toFixed(2))
    }

    const handleChangNetMinPerSqm = (netMinPerSqm) => {
        setValue(fieldNames.maxDiscount, formatDecimalNumber((100 - ((netMinPerSqm / netListedPerSqm) * 100)),2))
    }

    const pricing = calculateProductPricing(
        {
            listed_net_per_m2: netListedPerSqm?.toString() || "0",
            min_net_per_m2: minNetPerSqm?.toString() || "0",
            base_net_per_m2: baseNetPerSqm?.toString() || "0",
            vat_proc: vatPercent?.toString() || "0",
        },
        effectiveFootage || 0,
    );


    return (
        <TableContainer component={Paper}>
            <Table>
                <StyledTableHead>
                    <TableRow>
                        <StyledTableCell>{t("products.ProductPricing.price")}</StyledTableCell>
                        <StyledTableCell>
                            {t("products.ProductPricing.priceListedPerSqm")}
                        </StyledTableCell>
                        <StyledTableCell>
                            {t("products.ProductPricing.priceListed")}
                        </StyledTableCell>
                        <StyledTableCell>
                            {t("products.ProductPricing.priceMinSalePerSqm")}
                        </StyledTableCell>
                        <StyledTableCell>
                            {t("products.ProductPricing.priceMinSale")}
                        </StyledTableCell>
                        {!passportView && (
                            <>
                                <StyledTableCell>
                                    {t("products.ProductPricing.priceMinBasePerSQM")}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {t("products.ProductPricing.priceMinBase")}
                                </StyledTableCell>
                            </>
                        )}
                    </TableRow>
                </StyledTableHead>
                <TableBody>
                    <TableRow>
                        <TableCell>{t("products.ProductPricing.net")}</TableCell>
                        <ControlledNumberCell
                            control={control}
                            fieldName={fieldNames.listedNetPerSqm}
                            readOnly={readOnly}
                            onChangeCb={handleChangNetListedPerSqm}
                        />
                        <DummyNumericCell value={pricing.listed.net} />
                        <ControlledNumberCell
                            control={control}
                            fieldName={fieldNames.minNetPerSqm}
                            readOnly={readOnly}
                            onChangeCb={handleChangNetMinPerSqm}
                        />
                        <DummyNumericCell value={pricing.minSale.net} />

                        {!passportView && (
                            <>
                                <ControlledNumberCell
                                    control={control}
                                    fieldName={fieldNames.baseNetPerSqm}
                                    readOnly={readOnly}
                                />
                                <DummyNumericCell value={pricing.minBase.net} />
                            </>
                        )}
                    </TableRow>
                    <TableRow>
                        <TableCell>{t("products.ProductPricing.gross")}</TableCell>
                        <DummyNumericCell value={pricing.listed.gross_per_sqm} />
                        <DummyNumericCell value={pricing.listed.gross} />
                        <DummyNumericCell value={pricing.minSale.gross_per_sqm} />
                        <DummyNumericCell value={pricing.minSale.gross} />
                        {!passportView && (
                            <>
                                <DummyNumericCell value={pricing.minBase.gross_per_sqm} />
                                <DummyNumericCell value={pricing.minBase.gross} />
                            </>
                        )}
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const ControlledNumberCell = (props) => {
    const { control, fieldName, readOnly = false, onChangeCb } = props;

    return (
        <TableCell>
            <ControlledNumericInput
                variant="standard"
                control={control}
                fieldName={fieldName}
                readOnly={readOnly}
                onChangeCb={onChangeCb}
            />
        </TableCell>
    );
};

const ControlledNumericInput = (props) => {
    const { control, fieldName, readOnly = false, onChangeCb } = props;

    return (
        <Controller
            name={fieldName}
            control={control}
            render={({ field }) => (
                <TextField
                    {...field}
                    fullWidth
                    type="number"
                    variant={props.variant || "outlined"}
                    sx={
                        readOnly
                            ? { minWidth: "100px", ...props.sx, ...disabledInputSx }
                            : { minWidth: "100px", ...props.sx }
                    }
                    onChange={(e) => {
                        const fieldValue = e.target.value === "" ? "" : Number.parseFloat(e.target.value)
                        field.onChange(fieldValue)
                        if (onChangeCb){
                            onChangeCb(fieldValue)
                        }
                    }}
                    InputProps={{
                        startAdornment: props.percentage ? (
                            <InputAdornment position="start">%</InputAdornment>
                        ) : null,
                    }}
                    inputProps={
                        props.percentage ? { min: 0, max: 100, step: 0.01 } : { min: 0, step: 0.01 }
                    }
                    disabled={readOnly}
                />
            )}
        />
    );
};

const DummyNumericCell = (props) => {
    return (
        <TableCell>
            <TextField
                value={round(props.value, 2)}
                fullWidth
                type="number"
                variant={props.variant || "standard"}
                sx={{ minWidth: "100px", ...props.sx, ...disabledInputSx }}
                InputProps={{
                    startAdornment: props.percentage ? (
                        <InputAdornment position="start">%</InputAdornment>
                    ) : null,
                }}
                inputProps={props.percentage ? { min: 0, max: 100 } : undefined}
                disabled={true}
            />
        </TableCell>
    );
};
